import { css } from "@emotion/react";
import React from "react";
import { ShimmerEffectBox } from "../ShimmerEffect";
import { useTheme } from "../theme";

export interface FormHelperTextProps<V> {
  /**
   * @default false
   */
  invalid: boolean | undefined;
  loading: boolean | undefined;
  touched: boolean;
  value: V;
  children?: React.ReactNode | ((props: { value: V }) => React.ReactNode);
}

export const FormHelperText = <V,>({
  invalid = false,
  children,
  loading,
  value,
  touched,
  ...props
}: FormHelperTextProps<V>) => {
  const theme = useTheme();

  /**
   * If the input is not touched, do not show the error message.
   * But if it is not an error and is intended to help the user, display it.
   */
  if (touched === false && invalid) {
    return null;
  }

  const message =
    typeof children === "function" ? children({ value }) : children;

  if (message === undefined || message === null) {
    return null;
  }

  return (
    <span
      role={invalid ? "alert" : undefined}
      aria-live={invalid ? "assertive" : undefined}
      css={[
        theme.typography.caption,
        css`
          display: block;
          height: 19px;
          text-align: right;
        `,
        invalid
          ? css`
              color: ${theme.palette.intentions.error.main};
            `
          : undefined,
      ]}
      {...props}
    >
      {loading ? (
        <ShimmerEffectBox
          width={184}
          height={19}
          fontSize={theme.typography.caption.fontSize}
          inline
        />
      ) : (
        message
      )}
    </span>
  );
};
